import React from 'react';
import {
  Alert,
  Box,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFlow } from '../../contexts/FlowContext';
import { WebFlowLayout } from '../WebFlowLayout';
import { BookOptionCard } from '../components/BookOptionCard';
import { FlowContinueButton } from '../components/FlowContinueButton';
import {
  BOOK_OPTIONS_SELECTIONS_IMAGE,
  BOOK_SIZE_IMAGES,
  COVER_TYPE_IMAGES,
} from '../bookOptionsAssets';
import { BookSize, CoverType } from '../types';
import { StudioFlowColors } from '../studioFlowTheme';
import { SessionAPI } from '../../utils/sessionApi';

const BOOK_SIZES: BookSize[] = ['6x6', '8x8', '10x10'];
const COVER_TYPES: CoverType[] = ['Softcover', 'Hardcover'];
const formatBookSizeLabel = (size: BookSize): string => {
  const [width, height] = size.split('x');
  return `${width} x ${height}"`;
};

const formatContinueSummary = (bookSize: BookSize, coverType: CoverType): string =>
  `${bookSize}  |  ${coverType}`;

const SectionEyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontFamily: StudioFlowColors.bodyFontFamily,
      fontWeight: 700,
      fontSize: 12,
      lineHeight: 1.5,
      letterSpacing: 1,
      textTransform: 'uppercase',
      color: StudioFlowColors.eyebrow,
      textAlign: 'left',
    }}
  >
    {children}
  </Typography>
);

export const BookOptionsScreen: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { state, updateState } = useFlow();
  const [isSaving, setIsSaving] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState('');

  const handleBookSizeChange = (bookSize: BookSize) => {
    if (bookSize === '10x10' && state.coverType === 'Softcover') {
      updateState({ bookSize, coverType: 'Hardcover' });
      return;
    }

    updateState({ bookSize });
  };

  const handleCoverTypeChange = (coverType: CoverType) => {
    if (state.bookSize === '10x10' && coverType === 'Softcover') {
      setToastMessage('Softcover not available');
      return;
    }

    updateState({ coverType });
  };

  const handleContinue = async () => {
    setIsSaving(true);
    await SessionAPI.updateWebSession({
      sessionId: state.sessionId,
      bookSize: state.bookSize,
      coverType: state.coverType,
    });
    setIsSaving(false);
    navigate('/title');
  };

  return (
    <WebFlowLayout title="Pick your book options" maxWidth="lg">
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 7 },
          alignItems: { md: 'flex-start' },
          justifyContent: 'center',
          mt: { xs: 0, md: 2 },
          pb: { xs: 14, md: 0 },
        }}
      >
        <Box
          sx={{
            width: { xs: '100vw', md: 483 },
            mx: { xs: 'calc(50% - 50vw)', md: 0 },
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={BOOK_OPTIONS_SELECTIONS_IMAGE}
            alt="Chatbooks book size comparison"
            sx={{
              width: '100%',
              display: 'block',
              objectFit: 'contain',
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 3, md: 7 },
            width: { xs: '100%', md: 402 },
            maxWidth: 402,
            mx: { xs: 'auto', md: 0 },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <SectionEyebrow>Book size</SectionEyebrow>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {BOOK_SIZES.map((size) => (
                  <BookOptionCard
                    key={size}
                    label={formatBookSizeLabel(size)}
                    imageSrc={BOOK_SIZE_IMAGES[size]}
                    selected={state.bookSize === size}
                    onClick={() => handleBookSizeChange(size)}
                  />
                ))}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <SectionEyebrow>Cover type</SectionEyebrow>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {COVER_TYPES.map((cover) => (
                  <BookOptionCard
                    key={cover}
                    label={cover}
                    imageSrc={COVER_TYPE_IMAGES[cover]}
                    selected={state.coverType === cover}
                    onClick={() => handleCoverTypeChange(cover)}
                    disabled={state.bookSize === '10x10' && cover === 'Softcover'}
                    wide
                  />
                ))}
              </Box>
            </Box>
          </Box>

          {isDesktop && (
            <FlowContinueButton
              summary={formatContinueSummary(state.bookSize, state.coverType)}
              onClick={handleContinue}
              disabled={isSaving}
            />
          )}
        </Box>
      </Box>

      {!isDesktop && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            bgcolor: StudioFlowColors.background,
            px: 3,
            pt: 2,
            pb: 'calc(16px + env(safe-area-inset-bottom))',
          }}
        >
          <FlowContinueButton
            summary={formatContinueSummary(state.bookSize, state.coverType)}
            onClick={handleContinue}
            disabled={isSaving}
          />
        </Box>
      )}

      <Snackbar
        open={Boolean(toastMessage)}
        autoHideDuration={3000}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToastMessage('')}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </WebFlowLayout>
  );
};
